import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  EXPORT_EVENT,
  PROMO_MODAL_EVENT,
  bannerEarned,
  bannerSnoozed,
  isPromoModalOpen,
  lastExport,
  snoozeBanner,
} from "../digitizingServiceStorage";

// How long after a download finishes before the banner may slide in. The
// download itself is a click on a link the page made, so it has started by
// the time recordExport is called; this is just so the bar doesn't move the
// page under the pointer that clicked it.
const AFTER_DOWNLOAD_DELAY_MS = 4000;

interface DigitizingServiceBannerProps {
  // The digitizing_service flag, resolved true, with the service's config
  // loaded. Nothing renders, and nothing is counted, unless it is true.
  enabled: boolean;
  // The page on screen. A change is a "route change": a banner earned on the
  // previous page shows from here.
  page: string;
  // Pages it must not appear on: the quote form itself, /login.
  suppressed: boolean;
  // Free text from config ("$30"), or null to leave the hint off.
  priceFrom: string | null;
  // From config ("24 hours"), or null to leave the promise off.
  turnaround: string | null;
  snoozeHours: number;
  onGetQuote: () => void;
}

/**
 * The slim bar above the navigation offering custom digitizing, shown to
 * everyone on /create, and elsewhere once this browser has exported a
 * design. In flow rather than fixed, so it pushes
 * the page down instead of covering a download button, and it slides open
 * rather than appearing at full height in one frame.
 */
export function DigitizingServiceBanner({
  enabled,
  page,
  suppressed,
  priceFrom,
  turnaround,
  snoozeHours,
  onGetQuote,
}: DigitizingServiceBannerProps) {
  const posthog = usePostHog();
  // Whether enough has happened since the latest export: a page load or
  // route change, or a few seconds after the download. bannerEarned decides
  // whether that export counts at all (the snooze).
  const [ready, setReady] = useState(true);
  const [promoOpen, setPromoOpen] = useState(isPromoModalOpen);
  const [closed, setClosed] = useState(false);
  // /create shows it to every visitor, export or not; only a snooze hides it.
  const onCreate = page === "create";
  // Counts arrivals on /create, so it's reported shown once per visit.
  const [createVisit, setCreateVisit] = useState(0);

  useEffect(() => {
    setReady(true);
    if (page === "create") setCreateVisit((n) => n + 1);
  }, [page]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onExport = () => {
      // Held back while the download is under way, even if the bar was
      // already earned, so nothing moves under the pointer that clicked.
      setReady(false);
      // A new export is a new chance: if storage worked, bannerEarned still
      // holds it back until the snooze is over.
      setClosed(false);
      clearTimeout(timer);
      timer = setTimeout(() => setReady(true), AFTER_DOWNLOAD_DELAY_MS);
    };
    const onPromo = () => setPromoOpen(isPromoModalOpen());
    window.addEventListener(EXPORT_EVENT, onExport);
    window.addEventListener(PROMO_MODAL_EVENT, onPromo);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(EXPORT_EVENT, onExport);
      window.removeEventListener(PROMO_MODAL_EVENT, onPromo);
    };
  }, []);

  // Storage is re-read on every render rather than held in state, so a
  // request sent or a dismissal in another tab is honoured on the next
  // route change here.
  // On /create it is already up before any download, so a download there
  // doesn't take it down for the delay -- that would move the page instead.
  const earned = onCreate ? !bannerSnoozed(snoozeHours) : bannerEarned(snoozeHours);
  const visible =
    enabled && (ready || onCreate) && !closed && !suppressed && !promoOpen && earned;
  const exported = lastExport();
  const triggerPath = onCreate ? "create" : (exported?.trigger ?? null);

  // Once per /create visit, or elsewhere once per export that earned it,
  // however often it re-renders or comes back from a route it is suppressed
  // on.
  // On /create the key waits for the visit to be counted, or the render
  // before the count and the one after would each report it.
  const shownKey = onCreate
    ? createVisit > 0 && `create:${createVisit}`
    : exported && `export:${exported.at}`;
  const shownFor = useRef<string | false | null>(null);
  useEffect(() => {
    if (!visible || !shownKey || shownFor.current === shownKey) return;
    shownFor.current = shownKey;
    posthog?.capture("digitizing_banner_shown", { trigger_path: triggerPath });
  }, [visible, shownKey, triggerPath, posthog]);

  if (!visible) return null;

  const handleDismiss = () => {
    snoozeBanner();
    setClosed(true);
    posthog?.capture("digitizing_banner_dismissed", { trigger_path: triggerPath });
  };

  return (
    <section className="digitizing-banner" aria-label="Custom digitizing service">
      <div className="digitizing-banner-inner container">
        <p>
          Need a logo or image as an embroidery file? We'll digitize it for you.
          {turnaround && ` Quote within ${turnaround}.`}
          {priceFrom && <span className="digitizing-banner-price"> From {priceFrom}.</span>}
        </p>
        <a
          href="/digitizing-service?from=banner"
          className="digitizing-banner-cta"
          onClick={(e) => {
            e.preventDefault();
            posthog?.capture("digitizing_banner_clicked", { trigger_path: triggerPath });
            onGetQuote();
          }}
        >
          Get a quote →
        </a>
        <button
          type="button"
          className="digitizing-banner-close"
          aria-label="Dismiss digitizing service banner"
          onClick={handleDismiss}
        >
          ✕
        </button>
      </div>
    </section>
  );
}
